import React, { useEffect, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Input, message } from "antd";
import { WarningFilled } from "@ant-design/icons";
import { AppContext } from "../../contexts/AppContext";
import SubHeader from "../../components/SubHeader/SubHeader";
import GeneralButton from "../../components/GeneralButton/GeneralButton";
import { GENDER_TYPES } from "../../config/setting";
import Toast from "../../components/Toast/Toast";
import { registReservation } from "../../services/service";
import { createDateMessage, getConstValue } from "../../utils/commonUtils";
import dayjs from "dayjs";
import "../../styles/globals.css";
import "./ReservationConfirmPage.css";

const { TextArea } = Input;

/**
 * 予約内容確認画面用のコンポーネント
 * @param {} なし
 * @return {}  なし
 */
function ReservationConfirmPage() {
  const {
    globalMedicalInstitutionId,
    globalMedicalInstitutionName,
    globalSelectMenu,
    globalSelectAppointment,
    globalSelectPatient,
    globalSelectReservationDateTime,
    globalReservationInput,
    setGlobalQuestionnaireToken,
    setGlobalReservationToken,
    setGlobalEmailAddress,
    globalLoginUserInfo,
    globalLoading,
  } = useContext(AppContext);
  const navigate = useNavigate();
  const [selectInfoDisp, setSelectInfoDisp] = useState(null); // 表示用選択情報
  const [note, setNote] = useState(""); // テキストエリアの状態管理

  const [errorMessage, setErrorMessage] = useState(null);

  /**
   * コンポーネントがマウントされた時にデータを取得
   */
  useEffect(() => {
    const loadDatas = async () => {
      window.scrollTo(0, 0);
      let name = "";
      let kana = "";
      let birthday = "";
      let gender = "";
      let email = "";
      let tel = "";
      if (globalSelectPatient) {
        name =
          globalSelectPatient?.kanjiLast +
          " " +
          globalSelectPatient?.kanjiFirst;
        kana =
          globalSelectPatient?.kanaLast + " " + globalSelectPatient?.kanaFirst;
        birthday = dayjs(globalSelectPatient?.birthday).format(
          "YYYY年MM月DD日"
        );
        gender = getConstValue(GENDER_TYPES, globalSelectPatient?.sex, "name");
      } else {
        name =
          globalReservationInput?.kanjiLast +
          " " +
          globalReservationInput?.kanjiFirst;
        kana =
          globalReservationInput?.kanaLast +
          " " +
          globalReservationInput?.kanaFirst;
        birthday = dayjs(globalReservationInput?.birthday).format(
          "YYYY年MM月DD日"
        );
        gender = getConstValue(
          GENDER_TYPES,
          globalReservationInput?.sex,
          "name"
        );
        email = globalReservationInput?.email;
        tel = globalReservationInput?.tel;
      }

      const infoDisp = {
        date: createDateMessage(globalSelectReservationDateTime?.date),
        time: globalSelectReservationDateTime?.time,
        department: globalSelectMenu?.reservationMenuName,
        appointment: globalSelectAppointment?.name,
        name,
        kana,
        birthday,
        gender,
        email,
        tel,
        zipCode: globalReservationInput?.zipCode,
        address:
          globalReservationInput?.prefecture +
          globalReservationInput?.city +
          globalReservationInput?.street +
          (globalReservationInput?.building || ""),
      };

      setSelectInfoDisp(infoDisp);
    };

    if (!globalLoading) {
      loadDatas();
    }
  }, [globalLoading]);

  /**
   * 予約確定ボタンクリック時
   * @param {} なし
   * @return {} なし
   */
  const handleConfirmClick = async () => {
    const reservationInfo = {
      medicalInstitutionId: globalMedicalInstitutionId,
      reservationDateTime: `${
        globalSelectReservationDateTime?.date
      }T${globalSelectReservationDateTime?.time.split("-")[0].trim()}:00`,
      reservationMenuId: globalSelectMenu?.reservationMenuId,
      isTemporaryTimeSlot: globalSelectReservationDateTime?.isTemporaryTimeSlot,
      timeSlotId: globalSelectReservationDateTime?.timeSlotId,
      appointmentStatus: globalSelectAppointment?.id,
      note,
    };
    let patientInfo = "";

    if (globalSelectPatient) {
      const newPatientInfo = {
        ...globalSelectPatient,
        prefecture: globalLoginUserInfo.userInfo.prefecture,
        city: globalLoginUserInfo.userInfo.city,
        building: globalLoginUserInfo.userInfo.building,
        street: globalLoginUserInfo.userInfo.street,
        zipCode: globalLoginUserInfo.userInfo.zipCode,
        tel: globalLoginUserInfo.userInfo.tel,
        email: globalLoginUserInfo.userInfo.email,
      };
      patientInfo = newPatientInfo;
    } else {
      patientInfo = globalReservationInput;
    }

    // 予約確定
    try {
      const data = await registReservation(reservationInfo, patientInfo);

      if (data.result.status === "OK") {
        // 予約IDセット
        setGlobalReservationToken(data?.data?.reservationToken);

        // 問診IDセット
        setGlobalQuestionnaireToken(data?.data?.questionnaireToken);

        // メールアドレスセット
        if (globalLoginUserInfo) {
          setGlobalEmailAddress(globalLoginUserInfo.userInfo.email);
          console.log(
            "globalLoginUserInfo.userInfo.email",
            globalLoginUserInfo.userInfo.email
          );
        } else {
          setGlobalEmailAddress(selectInfoDisp?.email);
          console.log("selectInfoDisp?.email", selectInfoDisp?.email);
        }

        // 予約完了画面に遷移
        navigate("/reservation-done");
      } else {
        // エラーメッセージセット
        setErrorMessage(data?.result?.error_message);
      }
    } catch (error) {
      console.error("Error reservationConfirm:", error);
      showToast("予約確定に失敗しました", "error");
    }
  };

  /**
   * 戻るボタンクリック時
   * @param {string} path - パス
   * @return {} なし
   */
  const handleBackClick = (path) => {
    navigate(`/${path}`);
  };

  // トースト通知を表示する関数
  const showToast = (content, type) => {
    const key = `open${Date.now()}`;
    const closeToast = () => {
      message.destroy(key);
    };

    message.open({
      content: <Toast content={content} type="check" onClose={closeToast} />,
      key,
      className: `ant-message-notice-content-${type}`,
    });
  };

  return (
    <div className="reservation-confirm">
      <SubHeader clinicName={globalMedicalInstitutionName} />
      <div className="main-area">
        <div className="title">予約内容確認</div>
        {errorMessage && (
          <div className="error-area">
            <div className="icon-area">
              <WarningFilled className="icon" />
            </div>
            <label>{errorMessage}</label>
          </div>
        )}
        <div className="detail">
          <span className="emphasis-message">まだ予約は完了していません。</span>
          <br />
          必要情報をご確認の上、画面下部の予約確定ボタンから、予約を完了させてください
        </div>
        <div className="card-area">
          {selectInfoDisp && (
            <div className="card">
              <div className="card-title">{globalMedicalInstitutionName}</div>
              <div className="card-item">
                <div className="card-item-name">予約日時</div>
                <div className="card-item-value">
                  {selectInfoDisp.date}
                  <br />
                  {selectInfoDisp.time}
                </div>
              </div>
              <div className="card-item">
                <div className="card-item-name">診療科</div>
                <div className="card-item-value">
                  {selectInfoDisp.department}
                </div>
              </div>
              <div className="card-item">
                <div className="card-item-name">初再診</div>
                <div className="card-item-value">
                  {selectInfoDisp.appointment}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="card-area">
          {selectInfoDisp && (
            <div className="card">
              <div className="card-title">受診者情報</div>
              <div className="card-item">
                <div className="card-item-name">氏名</div>
                <div className="card-item-value">{selectInfoDisp.name}</div>
              </div>
              <div className="card-item">
                <div className="card-item-name">フリガナ</div>
                <div className="card-item-value">{selectInfoDisp.kana}</div>
              </div>
              <div className="card-item">
                <div className="card-item-name">生年月日</div>
                <div className="card-item-value">{selectInfoDisp.birthday}</div>
              </div>
              <div className="card-item">
                <div className="card-item-name">性別</div>
                <div className="card-item-value">{selectInfoDisp.gender}</div>
              </div>
              {!globalSelectPatient && (
                <div className="card-item">
                  <div className="card-item-name">メールアドレス</div>
                  <div className="card-item-value">{selectInfoDisp.email}</div>
                </div>
              )}
              {!globalSelectPatient && (
                <div className="card-item">
                  <div className="card-item-name">電話番号</div>
                  <div className="card-item-value">{selectInfoDisp.tel}</div>
                </div>
              )}
              {/* <div className="card-item">
                <div className="card-item-name">郵便番号</div>
                <div className="card-item-value">{selectInfoDisp.zipCode}</div>
              </div>
              <div className="card-item">
                <div className="card-item-name">住所</div>
                <div className="card-item-value">{selectInfoDisp.address}</div>
              </div> */}
            </div>
          )}
        </div>
        <div className="remarks-label">
          {"医療機関にお伝えしたいこと(任意)"}
        </div>
        <TextArea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="例)最近、新型コロナワクチンを接種いたしました。"
          rows={5}
        />
        <GeneralButton
          buttonLabel={"予約確定"}
          type={"primary-blue"}
          onClick={() => handleConfirmClick()}
        />
        {globalSelectPatient ? (
          <GeneralButton
            buttonLabel={"日時選択に戻る"}
            type={"secondary-white"}
            onClick={() => handleBackClick("datetime-select")}
          />
        ) : (
          <GeneralButton
            buttonLabel={"受診者情報入力に戻る"}
            type={"secondary-white"}
            onClick={() => handleBackClick("reservation-entry")}
          />
        )}
      </div>
    </div>
  );
}

export default ReservationConfirmPage;
